import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Users, Copy, UserPlus, Trash2, Check, X, Mail } from "lucide-react";
import { MobileShell } from "@/components/MobileShell";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import {
  listMyFamilyGroups,
  createFamilyGroup,
  joinFamilyByCode,
  listFamilyMembers,
  getFamilyBalance,
  removeMember,
  inviteByCashtag,
  listMyPendingInvites,
  acceptInvite,
  declineInvite,
  type FamilyGroup,
  type FamilyMember,
  type FamilyBalance,
  type FamilyPendingInvite,
} from "@/domains/family";
import { formatMoney } from "@/lib/money";

export default function Family() {
  const { user } = useAuth();
  const [groups, setGroups] = useState<FamilyGroup[]>([]);
  const [selected, setSelected] = useState<FamilyGroup | null>(null);
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [memberProfiles, setMemberProfiles] = useState<Record<string, { display_name: string | null; cashtag: string | null }>>({});
  const [balance, setBalance] = useState<FamilyBalance | null>(null);
  const [pending, setPending] = useState<FamilyPendingInvite[]>([]);
  const [newName, setNewName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [inviteTag, setInviteTag] = useState("");
  const [loading, setLoading] = useState(true);

  const refreshGroups = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listMyFamilyGroups();
      setGroups(list);
      setSelected((prev) => prev ?? list[0] ?? null);
    } catch (e: any) {
      toast.error(e.message ?? "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshPending = useCallback(async () => {
    try { setPending(await listMyPendingInvites()); } catch {}
  }, []);

  const refreshSelected = useCallback(async () => {
    if (!selected) { setMembers([]); setBalance(null); setMemberProfiles({}); return; }
    try {
      const ms = await listFamilyMembers(selected.id);
      setMembers(ms);
      const ids = ms.map(m => m.user_id);
      if (ids.length) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("user_id, display_name, cashtag")
          .in("user_id", ids);
        const map: Record<string, { display_name: string | null; cashtag: string | null }> = {};
        (profs ?? []).forEach((p: any) => { map[p.user_id] = { display_name: p.display_name, cashtag: p.cashtag }; });
        setMemberProfiles(map);
      }
    } catch {}
    try { setBalance(await getFamilyBalance(selected.id)); } catch { setBalance(null); }
  }, [selected]);

  useEffect(() => { refreshGroups(); refreshPending(); }, [refreshGroups, refreshPending]);
  useEffect(() => { refreshSelected(); }, [refreshSelected]);

  // Realtime: any change to my membership rows → refresh
  useEffect(() => {
    if (!user) return;
    const ch = supabase
      .channel(`family-${user.id}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "family_members", filter: `user_id=eq.${user.id}` },
        () => { refreshGroups(); refreshPending(); refreshSelected(); })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [user, refreshGroups, refreshPending, refreshSelected]);

  async function handleCreate() {
    if (!newName.trim()) return;
    try {
      const g = await createFamilyGroup(newName.trim());
      setNewName(""); toast.success("Family group created");
      setSelected(g); refreshGroups();
    } catch (e: any) { toast.error(e.message); }
  }

  async function handleJoin() {
    if (!joinCode.trim()) return;
    try {
      await joinFamilyByCode(joinCode.trim().toUpperCase());
      setJoinCode(""); toast.success("Joined family"); refreshGroups();
    } catch (e: any) { toast.error(e.message); }
  }

  async function handleInvite() {
    if (!selected || !inviteTag.trim()) return;
    try {
      await inviteByCashtag(selected.id, inviteTag.trim());
      setInviteTag(""); toast.success("Invite sent");
      refreshSelected();
    } catch (e: any) { toast.error(e.message); }
  }

  async function handleAccept(gid: string) {
    try { await acceptInvite(gid); toast.success("Joined!"); refreshGroups(); refreshPending(); }
    catch (e: any) { toast.error(e.message); }
  }

  async function handleDecline(gid: string) {
    try { await declineInvite(gid); toast("Invite declined"); refreshPending(); }
    catch (e: any) { toast.error(e.message); }
  }

  async function handleRemove(id: string) {
    if (!confirm("Remove this member?")) return;
    try {
      await removeMember(id); toast.success("Member removed");
      refreshSelected();
    } catch (e: any) { toast.error(e.message); }
  }

  function copyCode() {
    if (!selected) return;
    navigator.clipboard.writeText(selected.invite_code);
    toast.success("Invite code copied");
  }

  const isOwner = selected?.owner_id === user?.id;

  return (
    <MobileShell>
      <div className="min-h-screen bg-background pb-24">
        <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
          <div className="px-4 py-3 flex items-center gap-3">
            <Link to="/wallet" className="p-2 -ml-2"><ArrowLeft className="w-5 h-5" /></Link>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              <h1 className="text-lg font-semibold">Family Plans</h1>
            </div>
          </div>
        </header>

        <div className="p-4 space-y-4">
          {/* Pending invites */}
          {pending.length > 0 && (
            <Card className="p-4 space-y-3 border-primary/40">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Mail className="w-4 h-4 text-primary" />
                Pending invites ({pending.length})
              </div>
              <ul className="space-y-2">
                {pending.map((p) => (
                  <li key={p.group_id} className="flex items-center justify-between gap-2 text-sm">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{p.group_name}</div>
                      <div className="text-xs text-muted-foreground">
                        from {p.owner_cashtag ? `$${p.owner_cashtag}` : "owner"} · role: {p.role}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" onClick={() => handleAccept(p.group_id)}>
                        <Check className="w-4 h-4 mr-1" />Accept
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDecline(p.group_id)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {/* Create / Join */}
          <Card className="p-4 space-y-3">
            <div className="text-sm font-medium">Create a family</div>
            <div className="flex gap-2">
              <Input placeholder="Family name" value={newName} onChange={(e) => setNewName(e.target.value)} />
              <Button onClick={handleCreate}>Create</Button>
            </div>
            <div className="text-sm font-medium pt-2">Join with invite code</div>
            <div className="flex gap-2">
              <Input placeholder="ABCD1234" value={joinCode} onChange={(e) => setJoinCode(e.target.value)} />
              <Button variant="outline" onClick={handleJoin}><UserPlus className="w-4 h-4 mr-1" />Join</Button>
            </div>
          </Card>

          {/* Group switcher */}
          {groups.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {groups.map((g) => (
                <Button
                  key={g.id}
                  size="sm"
                  variant={selected?.id === g.id ? "default" : "outline"}
                  onClick={() => setSelected(g)}
                >
                  {g.name}
                </Button>
              ))}
            </div>
          )}

          {/* Selected group */}
          {selected && (
            <>
              <Card className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-muted-foreground">Family wallet</div>
                    <div className="text-2xl font-bold">
                      {balance ? formatMoney(balance.total_balance_cents) : "—"}
                    </div>
                    {balance && balance.total_locked_cents > 0 && (
                      <div className="text-xs text-muted-foreground">
                        {formatMoney(balance.total_locked_cents)} locked in vaults
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">Members</div>
                    <div className="text-2xl font-semibold">{balance?.member_count ?? members.length}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-2 border-t">
                  <div className="text-xs">
                    <div className="text-muted-foreground">Invite code</div>
                    <div className="font-mono font-semibold">{selected.invite_code}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={copyCode}>
                    <Copy className="w-4 h-4 mr-1" />Copy
                  </Button>
                </div>
              </Card>

              {/* Invite by cashtag (owner only) */}
              {isOwner && (
                <Card className="p-4 space-y-2">
                  <div className="text-sm font-medium">Invite by cashtag</div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="$username"
                      value={inviteTag}
                      onChange={(e) => setInviteTag(e.target.value)}
                    />
                    <Button onClick={handleInvite}>
                      <UserPlus className="w-4 h-4 mr-1" />Invite
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    They'll see the invite in their Family page and can accept instantly.
                  </p>
                </Card>
              )}

              <Card className="p-4">
                <div className="text-sm font-medium mb-3">
                  Members
                  {members.some(m => m.status === 'invited') && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      ({members.filter(m => m.status === 'invited').length} pending)
                    </span>
                  )}
                </div>
                {members.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No members yet.</div>
                ) : (
                  <ul className="space-y-2">
                    {members.map((m) => {
                      const prof = memberProfiles[m.user_id];
                      const label = m.user_id === user?.id
                        ? "You"
                        : prof?.display_name || (prof?.cashtag ? `$${prof.cashtag}` : m.user_id.slice(0, 8) + "…");
                      return (
                        <li key={m.id} className="flex items-center justify-between text-sm">
                          <div className="min-w-0">
                            <div className="font-medium truncate">{label}</div>
                            <div className="text-xs text-muted-foreground capitalize">
                              {prof?.cashtag && m.user_id !== user?.id && <span className="font-mono mr-1">${prof.cashtag}</span>}
                              {m.role} · <span className={m.status === 'invited' ? 'text-amber-500' : ''}>{m.status}</span>
                            </div>
                          </div>
                          {isOwner && m.user_id !== user?.id && (
                            <Button size="sm" variant="ghost" onClick={() => handleRemove(m.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Card>
            </>
          )}

          {!loading && groups.length === 0 && pending.length === 0 && (
            <Card className="p-6 text-center text-sm text-muted-foreground">
              You're not in any family yet. Create one above or join with a code.
            </Card>
          )}
        </div>
      </div>
    </MobileShell>
  );
}
